// Types for the IP Case Sheet — the shapes backend/routes/ip/* returns.
//
// Written against the API, not the database: fields the routes join in
// (author_name, prescriber_name) are here, and columns the routes never
// return are not. See HMS_V4_IP_CASE_SHEET_DESIGN.md §3-4.

// ── Shared vocabulary ────────────────────────────────────────────

export type AlertSeverity = 'red' | 'amber' | 'info';
export type AlertCategory = 'vital' | 'medication' | 'documentation' | 'result' | 'fluid';

/**
 * The click-through target on any summary item or alert.
 *
 * Every derived value the Case Sheet shows carries one of these, so a
 * clinician can always get from a claim to the record behind it. That is the
 * difference between a summary and an assertion.
 */
export interface SourceRef {
  type: string;
  id: number | null;
  at?: string;
  related?: { type: string; id: number };
  related_ids?: number[];
}

export interface ClinicalAlert {
  rule_key: string;
  label: string;
  category: AlertCategory;
  severity: AlertSeverity;
  message: string;
  detail: string | null;
  source: SourceRef | null;
  /** The published basis the threshold came from — shown, never hidden. */
  clinical_basis: string;
  rule_version: number;
}

export type NoteStatus = 'draft' | 'signed' | 'amended';
export type Shift = 'morning' | 'evening' | 'night';
export type ClinicalSource = 'manual' | 'ai_assisted';

// ── Case sheet aggregate ─────────────────────────────────────────

export interface Vitals {
  id: number;
  /** TIME TAKEN — when the reading was measured. Every chart and alert
   * plots/evaluates this, never entered_at (KMC #36). */
  recorded_at: string;
  /** TIME ENTERED — when HMS received it; server clock. */
  entered_at?: string | null;
  /** Entered later than the hospital's lag policy allowed at the time. */
  late_entry?: boolean;
  late_entry_reason?: string | null;
  /** Amendment chain — see POST /ip/vitals/:id/amend. */
  version?: number;
  amends_vitals_id?: number | null;
  superseded_by_vitals_id?: number | null;
  amendment_reason?: string | null;
  admission_id?: number | null;
  visit_id?: number | null;
  bp_systolic: number | null;
  bp_diastolic: number | null;
  pulse_rate: number | null;
  respiratory_rate: number | null;
  temperature: string | number | null;
  /** Set when the server converted an OP intake reading from °F to °C — the value as recorded. */
  temperature_recorded_f?: number | null;
  spo2: number | null;
  pain_score: number | null;
  oxygen_flow_lpm: string | number | null;
  oxygen_delivery: string | null;
  weight_kg: string | number | null;
  height_cm: string | number | null;
  bmi: string | number | null;
  symptoms: string | null;
  recorded_by: string | null;
  recorded_by_name?: string | null;
}

export interface ActiveProblem {
  diagnosis: string;
  code_system?: string | null;
  code?: string | null;
  status?: string | null;
  source?: SourceRef;
}

export interface CaseSheetAllergy {
  id: number;
  substance: string;
  criticality: string | null;
  reaction: string | null;
  verification_status: string;
  source: SourceRef;
}

export interface MedicationOrderSummary {
  id: number;
  drug_name: string;
  strength: string | null;
  dose_quantity: string | number | null;
  dose_unit: string | null;
  route: string | null;
  frequency_code: string | null;
  frequency_text: string | null;
  is_prn: boolean;
  status: MedicationOrderStatus;
  start_datetime: string;
  prescriber_name: string | null;
  source: SourceRef;
}

export interface InvestigationSummary {
  id: number;
  investigation_name: string;
  investigation_type: string;
  status: string;
  result_status: string;
  is_critical: boolean;
  reviewed_at: string | null;
  ordered_at: string;
  /** The two honest result carriers — there is no structured result. */
  has_document: boolean;
  has_summary: boolean;
  source: SourceRef;
}

export interface ConsultSummary {
  id: number;
  specialty_name: string | null;
  status: ConsultStatus;
  priority: string;
  reason: string | null;
  requested_at: string;
  source: SourceRef;
}

export interface CarePlanSummary {
  id: number;
  category: string;
  description: string;
  status: CarePlanStatus;
  target_datetime: string | null;
  source: SourceRef;
}

export interface CaseSheet {
  admission: {
    id: number;
    /** Hospital-assigned IP number (IP/<FY>/<SEQ>); null for admissions created before IP numbering. */
    ip_number?: string | null;
    status: 'admitted' | 'discharged';
    admitted_at: string;
    discharged_at: string | null;
    admitting_doctor: string | null;
    admitted_by: string | null;
    /** Consultant in charge (admissions.consultant_user_id), from the Face sheet. */
    consultant_name?: string | null;
    /** Latest active code-status order; null when none has been written. */
    code_status?: { value: CodeStatusValue; ordered_at: string; source: SourceRef } | null;
    visit_id: number | null;
    branch_id: number;
    ward_id: number;
    ward_name: string;
    ward_type: string;
    bed_number: string | number;
    length_of_stay_hours: number;
    length_of_stay_days: number;
    transfer_count: number;
  };
  patient: {
    id: number;
    uhid: string;
    name: string;
    /** Mr./Mrs./Ms./Miss — display with lib/patientName.ts. */
    title?: string | null;
    gender: string;
    age: number | null;
    dob: string | null;
    mobile: string | null;
    blood_group: string | null;
    /** 'unknown' / 'provisional' for an ED placeholder not yet identified (null = verified). */
    identity_status?: 'unknown' | 'provisional' | 'verified' | null;
  };
  reason_for_admission: string | null;
  working_diagnosis: string | null;
  active_problems: ActiveProblem[];
  allergies: CaseSheetAllergy[];
  /**
   * Three-valued on purpose. 'reviewed-none' (asked, nothing found) and
   * 'not-reviewed' (nobody asked) are different clinical states, and only the
   * first is safe to act on.
   */
  allergy_status: 'recorded' | 'reviewed-none' | 'not-reviewed';
  latest_vitals: Vitals | null;
  prior_vitals: Vitals | null;
  medication_orders: MedicationOrderSummary[];
  investigations: InvestigationSummary[];
  consults: ConsultSummary[];
  care_plan: CarePlanSummary[];
  fluid_balance_24h: { input: number; output: number; net: number };
  last_progress_note: ProgressNote | null;
  last_nursing_note: NursingNote | null;
  assessment_status: NoteStatus | null;
  alerts: ClinicalAlert[];
  generated_at: string;
}

// ── Assessment ───────────────────────────────────────────────────

// Phase 3 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.6) — structured
// history/exam sub-shapes.
export interface ChiefComplaint { text: string; duration_value?: number; duration_unit?: 'hours' | 'days' | 'weeks' | 'months' | 'years'; }
export type TriFlag = 'yes' | 'no' | 'unknown';
export interface PastHistoryFlag { v: TriFlag; year?: number; remarks?: string; }
export type PastHistoryFlags = Partial<Record<
  'dm' | 'htn' | 'cad' | 'tb' | 'asthma_copd' | 'epilepsy' | 'thyroid' | 'ckd' | 'cancer', PastHistoryFlag
>>;
export interface Addiction { substance: string; quantity?: string; duration?: string; status: 'current' | 'ex' | 'never'; }
export interface PersonalHistory {
  diet?: string; appetite?: string; sleep?: string; bowel?: string; bladder?: string;
  addictions?: Addiction[];
}
export interface MenstrualObstetric {
  lmp?: string; cycle?: string; menopause?: boolean;
  gpla?: { g?: number; p?: number; l?: number; a?: number };
  edd?: string;
}
export type GeneralSignStatus = 'present' | 'absent' | 'not_assessed';
export interface GeneralSign { status: GeneralSignStatus; remarks?: string; }
export type GeneralSigns = Partial<Record<
  'pallor' | 'icterus' | 'cyanosis' | 'clubbing' | 'lymphadenopathy' | 'oedema' | 'dehydration', GeneralSign
>>;

export interface AdmissionAssessment {
  id: number;
  admission_id: number;
  patient_id: number;
  admission_reason: string | null;
  presenting_complaint: string | null;
  history_present_illness: string | null;
  past_medical_history: string | null;
  surgical_history: string | null;
  family_history: string | null;
  social_history: string | null;
  allergy_reviewed_at: string | null;
  allergy_reviewed_by: string | null;
  medication_reviewed_at: string | null;
  medication_reviewed_by: string | null;
  general_examination: string | null;
  systemic_examination: Record<string, string> | null;
  provisional_diagnosis: string | null;
  clinical_impression: string | null;
  initial_plan: string | null;
  risk_assessment: Record<string, string> | null;
  attending_doctor: string | null;
  assessed_at: string;
  status: 'draft' | 'signed';
  signed_by: string | null;
  signed_at: string | null;
  created_by: string | null;
  assessed_by_user_id: number | null;
  chief_complaints: ChiefComplaint[] | null;
  past_history_flags: PastHistoryFlags | null;
  personal_history: PersonalHistory | null;
  menstrual_obstetric: MenstrualObstetric | null;
  treatment_history: string | null;
  general_signs: GeneralSigns | null;
  gcs_e: number | null;
  gcs_m: number | null;
  gcs_v: number | null;
  differential_diagnosis: string | null;
  expected_los_days: number | null;
  counselling_done: boolean | null;
}

export type AssessmentInput = Partial<Omit<AdmissionAssessment,
  'id' | 'admission_id' | 'patient_id' | 'status' | 'signed_by' | 'signed_at' | 'created_by' | 'assessed_by_user_id'>>;

// ── Progress notes ───────────────────────────────────────────────

export type SymptomTrend = 'improving' | 'unchanged' | 'worsening' | 'unknown';

export interface PlanItem {
  category: 'medication' | 'investigation' | 'procedure' | 'consult' | 'monitoring' | 'discharge';
  text: string;
  owner?: string;
}

export interface ProgressNote {
  id: number;
  admission_id: number;
  note_datetime: string;
  note_type: string;
  subjective: string | null;
  overnight_events: string | null;
  symptom_trend: SymptomTrend | null;
  objective: string | null;
  examination_findings: Record<string, string> | null;
  investigation_summary: string | null;
  assessment: string | null;
  active_problems: ActiveProblem[] | null;
  clinical_impression: string | null;
  plan: string | null;
  plan_items: PlanItem[] | null;
  status: NoteStatus;
  signed_by: string | null;
  signed_at: string | null;
  /** Amendment chain — the original is never overwritten. */
  version: number;
  amends_note_id: number | null;
  superseded_by_note_id: number | null;
  amendment_reason: string | null;
  amended_by: string | null;
  amended_at: string | null;
  source: ClinicalSource;
  ai_provider: string | null;
  ai_model: string | null;
  created_by: string | null;
  created_by_user_id: number | null;
  author_name?: string | null;
}

export type ProgressNoteInput = Partial<Pick<ProgressNote,
  'note_datetime' | 'note_type' | 'subjective' | 'overnight_events' | 'symptom_trend' |
  'objective' | 'examination_findings' | 'investigation_summary' | 'assessment' |
  'active_problems' | 'clinical_impression' | 'plan' | 'plan_items'>> & {
  source?: ClinicalSource;
  ai_provider?: string;
  ai_model?: string;
};

// ── Nursing notes ────────────────────────────────────────────────

export interface NursingIntervention { intervention: string; at?: string; outcome?: string }

export interface NursingNote {
  id: number;
  admission_id: number;
  recorded_at: string;
  shift: Shift | null;
  note_type: 'assessment' | 'observation' | 'intervention' | 'handover' | 'incident';
  observations: string | null;
  interventions: NursingIntervention[] | null;
  patient_response: string | null;
  handover_notes: string | null;
  remarks: string | null;
  recorded_by: string | null;
  recorded_by_user_id: number | null;
  author_name?: string | null;
}

export type NursingNoteInput = Partial<Pick<NursingNote,
  'recorded_at' | 'shift' | 'note_type' | 'observations' | 'interventions' |
  'patient_response' | 'handover_notes' | 'remarks'>>;

// ── Medications ──────────────────────────────────────────────────

export type MedicationOrderStatus = 'active' | 'held' | 'discontinued' | 'completed' | 'cancelled';
export type AdministrationStatus = 'given' | 'not_given' | 'held' | 'refused' | 'cancelled';

export interface MedicationOrder extends MedicationOrderSummary {
  admission_id: number;
  medicine_id: number | null;
  code_system: string | null;
  code: string | null;
  administration_times: string[] | null;
  prn_indication: string | null;
  indication: string | null;
  instructions: string | null;
  stop_datetime: string | null;
  hold_reason: string | null;
  held_at: string | null;
  held_by: string | null;
  discontinue_reason: string | null;
  discontinued_at: string | null;
  discontinued_by: string | null;
  prescriber_id: number | null;
  prescribed_by: string | null;
  created_at: string;
}

/** One product match from GET /api/medicines?q= — see api.ts's searchMedicines. */
export interface MedicineSearchResult {
  id: number;
  generic_name: string;
  brand_name: string | null;
  strength: string | null;
  dosage_form: string | null;
}

export interface MedicationOrderInput {
  drug_name: string;
  medicine_id?: number | null;
  strength?: string;
  dose_quantity?: number | null;
  dose_unit?: string;
  route?: string;
  frequency_code?: string;
  frequency_text?: string;
  administration_times?: string[];
  is_prn?: boolean;
  prn_indication?: string;
  indication?: string;
  instructions?: string;
  start_datetime?: string;
  stop_datetime?: string | null;
  /** Explicit opt-in for an order that genuinely has no schedule. */
  unscheduled?: boolean;
}

/** POST /ip/medication-orders/:id/dispense — see routes/ip/medications.js. */
export interface DispenseOrderInput {
  items: Array<{ inventory_id: number; quantity: number; batch_selection_reason?: string; batch_selection_remarks?: string }>;
  prescriber_name?: string;
  prescriber_reg_no?: string;
  payment_mode?: string;
  /** Defaults 'Pending' server-side — settles at discharge unless paid now. */
  payment_status?: 'Paid' | 'Pending';
}

/** Advisory only — never blocks. null implied_quantity means no opinion. */
export interface OrderQuantityFlag {
  implied_quantity: number | null;
  total_after: number;
  exceeds: boolean;
}

export interface DispenseOrderResult {
  bill: { id: number; bill_number: string; total_amount: string | number };
  quantity_flag: OrderQuantityFlag;
}

export interface MedicationAdministration {
  id: number;
  order_id: number;
  admission_id: number;
  scheduled_datetime: string | null;
  administered_datetime: string | null;
  status: AdministrationStatus;
  dose_quantity: string | number | null;
  dose_unit: string | null;
  route: string | null;
  administered_by: string | null;
  administered_by_user_id: number | null;
  administered_by_name?: string | null;
  witnessed_by: string | null;
  not_given_reason: string | null;
  remarks: string | null;
}

export interface AdministrationInput {
  status: Exclude<AdministrationStatus, 'cancelled'>;
  scheduled_datetime?: string | null;
  administered_datetime?: string | null;
  dose_quantity?: number | null;
  dose_unit?: string;
  route?: string;
  not_given_reason?: string;
  witnessed_by?: string;
  remarks?: string;
}

/**
 * One cell of the MAR.
 *
 * `slot` (what the order says should happen) and `administration` (what a
 * nurse recorded) are separate fields, never collapsed. A slot with no
 * administration is 'due'/'missed' — meaning NOTHING WAS CHARTED — which is
 * not the same as 'not_given', and the UI must never render it as such.
 */
export interface MarDose {
  slot: string | null;
  scheduled_datetime: string | null;
  administration: MedicationAdministration | null;
  state: AdministrationStatus | 'upcoming' | 'due' | 'missed';
}

export interface MarRow { order: MedicationOrder; doses: MarDose[] }
export interface Mar { from: string; to: string; orders: MarRow[] }

// ── Fluid balance ────────────────────────────────────────────────

export type FluidDirection = 'input' | 'output';
export const FLUID_INPUT_CATEGORIES = ['oral', 'iv', 'blood-product', 'enteral', 'other'] as const;
export const FLUID_OUTPUT_CATEGORIES = ['urine', 'drain', 'vomitus', 'stool', 'blood-loss', 'other'] as const;

export interface FluidEntry {
  id: number;
  recorded_datetime: string;
  shift: Shift | null;
  direction: FluidDirection;
  category: string;
  quantity_ml: string | number;
  route_detail: string | null;
  remarks: string | null;
  recorded_by: string | null;
}

export interface FluidTotals { input: number; output: number; net: number }

export interface FluidBalance {
  entries: FluidEntry[];
  totals: { window: FluidTotals; last_24h: FluidTotals; cumulative: FluidTotals };
}

// ── Investigations, procedures, consults, care plan ──────────────

export interface Investigation extends InvestigationSummary {
  admission_id: number;
  code_system: string | null;
  code: string | null;
  priority: 'routine' | 'urgent' | 'stat';
  reason: string | null;
  ordered_by: string | null;
  ordered_by_name: string | null;
  result_summary: string | null;
  result_document_id: number | null;
  result_document_name: string | null;
  reviewed_by: string | null;
  reviewed_by_name: string | null;
}

export interface InvestigationInput {
  investigation_type: 'lab' | 'imaging' | 'cardiac' | 'pathology' | 'other';
  investigation_name: string;
  priority?: 'routine' | 'urgent' | 'stat';
  reason?: string;
  is_critical?: boolean;
  status?: string;
  result_status?: string;
  result_summary?: string;
  result_document_id?: number | null;
  ordered_at?: string;
}

export interface Procedure {
  id: number;
  admission_id: number;
  procedure_name: string;
  indication: string | null;
  performed_datetime: string;
  performer: string | null;
  performer_full_name: string | null;
  assistants: string | null;
  location: string | null;
  anaesthesia_type: string | null;
  findings: string | null;
  complications: string | null;
  outcome: 'successful' | 'partial' | 'abandoned' | 'complication' | null;
  notes: string | null;
  document_id: number | null;
  document_name: string | null;
  created_by: string | null;
}

export type ProcedureInput = Partial<Omit<Procedure, 'id' | 'admission_id' | 'document_name' | 'performer_full_name' | 'created_by'>>
  & { procedure_name: string };

export type ConsultStatus = 'requested' | 'accepted' | 'in-progress' | 'completed' | 'cancelled';

export interface Consult extends ConsultSummary {
  admission_id: number;
  specialty_id: number | null;
  specialty_current_name: string | null;
  consultant_user_id: number | null;
  consultant_name: string | null;
  consultant_full_name: string | null;
  clinical_question: string | null;
  requested_by: string | null;
  requested_by_name: string | null;
  accepted_at: string | null;
  completed_at: string | null;
  response_notes: string | null;
  recommendations: string | null;
}

export interface ConsultInput {
  reason: string;
  specialty_id?: number | null;
  specialty_name?: string;
  consultant_user_id?: number | null;
  clinical_question?: string;
  priority?: 'routine' | 'urgent' | 'emergency';
  requested_at?: string;
}

export type CarePlanStatus = 'planned' | 'in-progress' | 'completed' | 'cancelled' | 'on-hold';
export type CarePlanCategory =
  | 'monitoring' | 'medication' | 'nutrition' | 'mobility' | 'wound-care'
  | 'oxygen' | 'physiotherapy' | 'education' | 'discharge-preparation' | 'other';

export interface CarePlanActivity extends CarePlanSummary {
  admission_id: number;
  category: CarePlanCategory;
  owner_role: string | null;
  owner_user_id: number | null;
  owner_name: string | null;
  completed_at: string | null;
  completed_by: string | null;
  notes: string | null;
}

export interface CarePlanInput {
  category: CarePlanCategory;
  description: string;
  owner_role?: string;
  owner_user_id?: number | null;
  target_datetime?: string | null;
  notes?: string;
}

// ── Transfers ────────────────────────────────────────────────────

export interface BedTransfer {
  id: number;
  admission_id: number;
  from_bed_id: number | null;
  from_bed_number: string | null;
  from_ward_name: string | null;
  to_bed_id: number;
  to_bed_number: string;
  to_ward_name: string;
  reason: string | null;
  transferred_at: string;
  transferred_by: string | null;
}

// ── Timeline ─────────────────────────────────────────────────────

export type TimelineType =
  | 'admission' | 'assessment' | 'progress_note' | 'nursing_note' | 'vitals'
  | 'medication_order' | 'medication_administration' | 'investigation'
  | 'procedure' | 'consult' | 'care_plan' | 'transfer' | 'document';

export interface TimelineItem {
  type: TimelineType;
  id: number;
  at: string;
  title: string;
  detail: string | null;
  author: string | null;
  category: string;
  source: SourceRef;
}

export interface Timeline {
  data: TimelineItem[];
  limit: number;
  offset: number;
  has_more: boolean;
  window: { from: string; to: string };
}

// ── Gap check & discharge readiness ──────────────────────────────

export interface DocumentationGap {
  key: string;
  message: string;
  action: string;
  target: SourceRef;
}

export interface GapCheck { gaps: DocumentationGap[]; checked_at: string }

// ── Print profiles & case sheet export (Phase 6/7) ───────────────

export interface IpPrintProfile {
  code: 'mrd_legal' | 'insurance_tpa' | 'patient_copy' | 'interim' | string;
  label: string;
  watermark: string | null;
  include_payer: boolean;
  require_reason: boolean;
  finality_gate: boolean;
}

export interface CaseSheetDeficiency {
  key: string;
  message: string;
  source?: SourceRef | null;
}

export interface CaseSheetDeficiencies {
  blocking: CaseSheetDeficiency[];
  warnings: CaseSheetDeficiency[];
}

export interface MrdChecklist {
  deficiencies: CaseSheetDeficiencies;
  checked_at: string;
}

export interface CaseSheetExportRecipient {
  name: string;
  relation?: string;
  org?: string;
  reference?: string;
}

export interface CaseSheetExportInput {
  profile: IpPrintProfile['code'];
  purpose?: string;
  recipient?: CaseSheetExportRecipient;
  override_reason?: string;
}

export interface CaseSheetExportResult {
  export_id: number;
  document_id: number;
  page_count: number | null;
  content_hash: string;
  deficiencies: CaseSheetDeficiencies;
}

/** Thrown shape on a 409 (blocking deficiencies, no override yet) — the
 * dialog catches this specifically to show the override-reason field. */
export interface CaseSheetBlockedError {
  deficiencies: CaseSheetDeficiencies;
}

export interface ReadinessIndicator {
  key: string;
  state: 'ok' | 'attention' | 'unknown';
  detail: string;
  source: SourceRef | null;
}

/**
 * Deliberately has NO overall boolean. Discharge is a physician decision, and
 * a `ready: true` field is exactly what would get read as the system making it.
 */
export interface DischargeReadiness {
  indicators: ReadinessIndicator[];
  note: string;
  checked_at: string;
}

// ── AI gist ──────────────────────────────────────────────────────

export interface GistStatement {
  text: string;
  /** Resolved source records. Invented refs are dropped server-side. */
  sources: SourceRef[];
  /** True when nothing the model cited resolved — surfaced, never hidden. */
  unverified: boolean;
}

export type ClinicalGist =
  | {
      available: true;
      statements: GistStatement[];
      alerts: ClinicalAlert[];
      provenance: { provider: string; model: string; generated_at: string; kind: 'ai_summary'; advisory: true };
    }
  | { available: false; reason: string; alerts: ClinicalAlert[]; generated_at?: string };

// ── Face Sheet — admission particulars, attenders, MLC, payer ────
// Phase 1 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.1-6.4).

export type AdmissionType = 'emergency' | 'elective' | 'transfer-in' | 'day-care';
export type AdmissionSource = 'opd' | 'ed' | 'referral' | 'direct' | 'other';

/** Shape returned by both GET and PATCH .../particulars — every admissions
 * column plus the patient/bed/ward join withAdmission() already resolves. */
export interface AdmissionParticulars {
  id: number;
  ip_number: string | null;
  admission_type: AdmissionType | null;
  admission_source: AdmissionSource | null;
  referred_by: string | null;
  specialty_id: number | null;
  consultant_user_id: number | null;
  patient_name: string;
  patient_uhid: string;
  bed_number: string;
  ward_name: string;
  admitted_at: string;
  status: 'admitted' | 'discharged';
}

export interface ParticularsInput {
  admission_type?: AdmissionType;
  admission_source?: AdmissionSource;
  referred_by?: string;
  specialty_id?: number | null;
  consultant_user_id?: number | null;
}

export type IdProofType = 'aadhaar' | 'voter' | 'dl' | 'passport' | 'other';

export interface Attender {
  id: number;
  admission_id: number;
  patient_id: number;
  name: string;
  relationship: string | null;
  mobile: string | null;
  alt_mobile: string | null;
  address: string | null;
  is_primary: boolean;
  is_legal_representative: boolean;
  id_proof_type: IdProofType | null;
  created_at: string;
}

export interface AttenderInput {
  name: string;
  relationship?: string;
  mobile?: string;
  alt_mobile?: string;
  address?: string;
  is_primary?: boolean;
  is_legal_representative?: boolean;
  id_proof_type?: IdProofType;
}

export interface Mlc {
  admission_id: number;
  patient_id: number;
  is_mlc: boolean;
  mlc_number: string | null;
  category: string | null;
  incident_at: string | null;
  incident_place: string | null;
  informant: string | null;
  police_station: string | null;
  intimation_sent_at: string | null;
  intimation_mode: string | null;
  intimation_ref: string | null;
  police_arrived_at: string | null;
  officer_name: string | null;
  officer_id: string | null;
  evidence_preserved: Record<string, unknown> | null;
  injury_description: string | null;
  brought_by: { name?: string; relation?: string; contact?: string } | null;
  notes: string | null;
  updated_at: string;
}

export type MlcInput = Partial<Omit<Mlc, 'admission_id' | 'patient_id' | 'updated_at'>>;

export interface PayerScheme {
  code: string;
  label: string;
}

export interface AdmissionPayer {
  id: number;
  admission_id: number;
  scheme_code: string;
  insurer_or_tpa: string | null;
  policy_no: string | null;
  member_id: string | null;
  card_no: string | null;
  preauth_no: string | null;
  preauth_status: 'pending' | 'approved' | 'rejected' | null;
  employer: string | null;
  valid_from: string | null;
  valid_to: string | null;
  is_primary: boolean;
  created_at: string;
  // Insurance Claims Phase 1.4 (docs/HMS_V4_INSURANCE_CLAIMS_DESIGN.md §4.5) —
  // CS's "no amounts on this table" stays true: these are DECLARED policy
  // limits for the estimator, not billed/approved amounts.
  insurer_payer_id: number | null;
  tpa_payer_id: number | null;
  policy_type: 'individual' | 'floater' | 'group' | 'scheme' | null;
  proposer_name: string | null;
  relation_to_proposer: string | null;
  employee_id: string | null;
  sum_insured: number | null;
  balance_sum_insured: number | null;
  room_rent_limit_per_day: number | null;
  room_entitlement: string | null;
  copay_percent: number | null;
  deductible: number | null;
  other_policy_exists: boolean | null;
  other_policy_details: string | null;
  scheme_beneficiary_id: string | null;
  verification_ref: string | null;
  id_proof_type: 'aadhaar' | 'voter' | 'dl' | 'passport' | 'other' | null;
  id_proof_last4: string | null;
  id_verified_by_user_id: number | null;
  id_verified_at: string | null;
  // Present only on the response to a create/update — not persisted on the
  // row itself (set by utils/claimAutoCreate.js the moment a resolvable
  // insurer/TPA makes this coverage claim-worthy).
  claim_id?: number | null;
}

export interface PayerInput {
  scheme_code: string;
  insurer_or_tpa?: string;
  policy_no?: string;
  member_id?: string;
  card_no?: string;
  preauth_no?: string;
  preauth_status?: 'pending' | 'approved' | 'rejected';
  employer?: string;
  valid_from?: string;
  valid_to?: string;
  is_primary?: boolean;
  insurer_payer_id?: number | null;
  tpa_payer_id?: number | null;
  policy_type?: 'individual' | 'floater' | 'group' | 'scheme';
  proposer_name?: string;
  relation_to_proposer?: string;
  employee_id?: string;
  sum_insured?: number;
  room_rent_limit_per_day?: number;
  room_entitlement?: string;
  copay_percent?: number;
  deductible?: number;
  scheme_beneficiary_id?: string;
  verification_ref?: string;
  id_proof_type?: 'aadhaar' | 'voter' | 'dl' | 'passport' | 'other';
  id_proof_last4?: string;
}

/** The staff-facing payer-master picker — a subset of Admin's full Payer shape. */
export interface PayerMasterOption {
  id: number;
  payer_type: 'insurer' | 'tpa' | 'govt_scheme' | 'corporate' | 'psu' | 'other';
  code: string;
  name: string;
  active: boolean;
}

// ── Consent register ──────────────────────────────────────────────
// Phase 2 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§5.12/§6.5).
// Append-only: there is no "edit" — a withdrawal is a new row.

export type ConsentType =
  | 'general_admission' | 'treatment' | 'procedure' | 'anaesthesia' | 'blood' | 'high_risk'
  | 'hiv_test' | 'media_research' | 'data_ai' | 'lama_dama' | 'dnr' | 'refusal';
export type ConsentStatus = 'obtained' | 'refused' | 'withdrawn' | 'pending';
export type ConsentGivenBy = 'patient' | 'lar' | 'guardian';
export type ConsentSignatureMode = 'wet' | 'thumb' | 'esign' | 'otp';

export interface Consent {
  id: number;
  admission_id: number;
  patient_id: number;
  consent_type: ConsentType;
  template_id: number | null;
  procedure_id: number | null;
  procedure_text: string | null;
  status: ConsentStatus;
  language: string | null;
  explained_by_user_id: number | null;
  explained_by_name: string | null;
  risks_alternatives_explained: boolean;
  given_by: ConsentGivenBy;
  signatory_name: string | null;
  signatory_relation: string | null;
  signature_mode: ConsentSignatureMode | null;
  witness_name: string | null;
  witness_relation: string | null;
  interpreter_name: string | null;
  document_id: number | null;
  obtained_at: string;
  valid_until: string | null;
  reason: string | null;
  supersedes_id: number | null;
  /** True when a later row (a withdrawal) points back at this one. */
  superseded: boolean;
  created_at: string;
}

export interface ConsentInput {
  consent_type: ConsentType;
  procedure_id?: number;
  procedure_text?: string;
  status?: ConsentStatus;
  language?: string;
  explained_by_user_id?: number;
  risks_alternatives_explained?: boolean;
  given_by: ConsentGivenBy;
  signatory_name?: string;
  signatory_relation?: string;
  signature_mode?: ConsentSignatureMode;
  witness_name?: string;
  witness_relation?: string;
  interpreter_name?: string;
  obtained_at?: string;
  valid_until?: string;
  reason?: string;
}

// ── Nursing assessment & risk screens ──────────────────────────────
// Phase 3 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.7).

export interface NursingAssessment {
  admission_id: number;
  patient_id: number;
  arrival_mode: string | null;
  consciousness: string | null;
  skin_findings: string | null;
  lines_tubes: { type: string; site?: string; inserted_at?: string }[] | null;
  id_band_at: string | null;
  allergy_band_at: string | null;
  education_needs: { language?: string; literacy?: string; barriers?: string; topics_taught?: string[]; teach_back_done?: boolean } | null;
  discharge_planning: { lives_alone?: boolean; caregiver?: string; equipment_needs?: string } | null;
  belongings: { items?: string[]; handed_over_to?: string; signature_note?: string } | null;
  assessed_at: string;
  status: 'draft' | 'signed';
  signed_by: string | null;
  signed_at: string | null;
}

export type NursingAssessmentInput = Partial<Omit<NursingAssessment,
  'admission_id' | 'patient_id' | 'status' | 'signed_by' | 'signed_at'>>;

export type RiskScreenType = 'pain' | 'nutrition' | 'fall' | 'pressure_injury' | 'vte' | 'functional';
/** Tools this build can score server-side — see utils/ipRiskScores.js. */
export type RiskScreenTool = 'nrs' | 'morse' | 'braden' | 'padua' | 'nrs2002' | 'must' | 'barthel';

export interface RiskScreen {
  id: number;
  admission_id: number;
  patient_id: number;
  screen_type: RiskScreenType;
  tool: RiskScreenTool;
  /** Always server-computed from `items` — never trust a client-held copy. */
  score: string;
  band: string;
  items: Record<string, unknown>;
  screened_at: string;
  screened_by_user_id: number | null;
  screened_by_name: string | null;
  next_due_at: string | null;
  remarks: string | null;
}

export interface RiskScreenInput {
  screen_type: RiskScreenType;
  tool: RiskScreenTool;
  items: Record<string, unknown>;
  next_due_at?: string;
  remarks?: string;
}

// ── Clinical orders (Part B1) & admission diagnoses ─────────────────
// Phase 4 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.8-6.9).

export type ClinicalOrderType =
  | 'diet' | 'activity' | 'monitoring' | 'oxygen' | 'isolation' | 'nursing' | 'code_status' | 'blood_request' | 'other';
export type ClinicalOrderStatus = 'active' | 'completed' | 'discontinued' | 'cancelled';
export type ClinicalOrderMode = 'written' | 'verbal' | 'telephonic';
export type CodeStatusValue = 'full_code' | 'dnar' | 'dni' | 'comfort_care';

export interface ClinicalOrder {
  id: number;
  admission_id: number;
  patient_id: number;
  order_type: ClinicalOrderType;
  text: string;
  frequency: string | null;
  start_datetime: string;
  stop_datetime: string | null;
  code_status_value: CodeStatusValue | null;
  linked_consent_id: number | null;
  status: ClinicalOrderStatus;
  mode: ClinicalOrderMode;
  ordered_by_user_id: number;
  ordered_by_name: string | null;
  /** The nurse who read a verbal/telephonic order back — the wireframe's B1 "Read-back" column. */
  read_back_by_user_id: number | null;
  read_back_by_name: string | null;
  countersigned_by_user_id: number | null;
  countersigned_by_name: string | null;
  countersigned_at: string | null;
  discontinued_at: string | null;
  discontinue_reason: string | null;
  amends_order_id: number | null;
  amendment_reason: string | null;
  /** Set once an amendment has been recorded against this order. */
  superseded_by_order_id: number | null;
  created_at: string;
}

export interface ClinicalOrderInput {
  order_type: ClinicalOrderType;
  text: string;
  frequency?: string;
  start_datetime?: string;
  stop_datetime?: string;
  code_status_value?: CodeStatusValue;
  linked_consent_id?: number;
  mode?: ClinicalOrderMode;
  ordered_by_user_id: number;
  /** Required unless mode is 'written'. */
  read_back_by_user_id?: number;
}

export type DiagnosisRole =
  | 'provisional' | 'differential' | 'working' | 'principal' | 'secondary' | 'comorbidity' | 'complication';

export interface AdmissionDiagnosis {
  id: number;
  admission_id: number;
  patient_id: number;
  role: DiagnosisRole;
  text: string;
  code_system: string | null;
  code: string | null;
  present_on_admission: boolean | null;
  recorded_by_user_id: number | null;
  recorded_by_name: string | null;
  recorded_at: string;
  /** Set once a correction (supersede) has been recorded against this row. */
  superseded_by_id: number | null;
  /** True when a later row (a correction) points back at this one. */
  superseded: boolean;
}

export interface DiagnosisInput {
  role: DiagnosisRole;
  text: string;
  code_system?: string;
  code?: string;
  present_on_admission?: boolean;
}

// ── Discharge summary (incl. death variant) ──────────────────────────
// Phase 5 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§6.11).

export type DischargeSummaryStatus = 'draft' | 'signed' | 'amended';
export type DischargeType = 'normal' | 'lama' | 'dama' | 'referred' | 'death';
export type DischargeMedicationStatus = 'new' | 'changed' | 'continued' | 'stopped';

export interface ConditionAtDischarge {
  vitals?: string;
  ambulation?: string;
  diet?: string;
  mental_status?: string;
}

export interface DischargeMedicationRow {
  drug_name: string;
  medicine_id?: number | null;
  dose?: string;
  route?: string;
  freq?: string;
  duration?: string;
  status: DischargeMedicationStatus;
  instruction?: string;
}

export interface FollowUpRow {
  when?: string;
  with?: string;
  where?: string;
  purpose?: string;
}

export interface PendingResultRow {
  test?: string;
  followed_by?: string;
}

export interface DischargeAdvice {
  diet?: string;
  activity?: string;
  wound_care?: string;
  other?: string;
}

export interface DeathDetails {
  at?: string;
  immediate_cause?: string;
  antecedent_cause?: string;
  underlying_cause?: string;
  mccd_no?: string;
  handed_over_to?: string;
  handed_over_at?: string;
  resus_ref?: string;
}

export interface DischargeSummary {
  id: number;
  admission_id: number;
  patient_id: number;
  version: number;
  status: DischargeSummaryStatus;
  discharge_type: DischargeType;
  final_diagnosis_ids: number[] | null;
  reason_for_admission: string | null;
  significant_findings: string | null;
  investigations_summary: string | null;
  procedures_summary: string | null;
  hospital_course: string | null;
  condition_at_discharge: ConditionAtDischarge | null;
  discharge_medications: DischargeMedicationRow[] | null;
  follow_up: FollowUpRow[] | null;
  pending_results: PendingResultRow[] | null;
  advice: DischargeAdvice | null;
  warning_signs: string | null;
  emergency_contact_text: string | null;
  advice_language: string | null;
  teach_back_done: boolean | null;
  copy_given_to: string | null;
  copy_given_at: string | null;
  death: DeathDetails | null;
  source: 'manual' | 'ai_assisted';
  ai_provider: string | null;
  ai_model: string | null;
  /** The full text of whatever document was last approved via the chat —
   *  a reference snapshot, never editable through the structured form and
   *  never the source for the admissions.discharge_summary back-fill (the
   *  structured fields are the richer, more current source by sign time). */
  ai_generated_text: string | null;
  /** The .docx patient_documents row this draft's text came from — set on
   *  every approve, follows the latest one. Null for a draft never synced
   *  from an approved document (e.g. typed straight into the structured
   *  form). Backs the "👁 View / ⬇ Download" link via DocumentPreviewButtons. */
  document_id: number | null;
  amends_id: number | null;
  superseded_by_id: number | null;
  amendment_reason: string | null;
  signed_by: string | null;
  signed_by_user_id: number | null;
  signed_at: string | null;
  created_at: string;
  /** Set on the sign response only, when discharge_type='death' and
   *  recordDeceasedTx could not mark the patient deceased (e.g. an
   *  out-of-range time of death) — the signature itself still succeeded. */
  deceased_flag_warning?: string | null;
}

export type DischargeSummaryInput = Partial<Omit<DischargeSummary,
  'id' | 'admission_id' | 'patient_id' | 'version' | 'status' | 'amends_id' | 'superseded_by_id'
  | 'signed_by' | 'signed_by_user_id' | 'signed_at' | 'created_at'>>;

// ── Alert rule config ────────────────────────────────────────────

export interface AlertRule {
  id: number;
  rule_key: string;
  label: string;
  category: AlertCategory;
  severity: AlertSeverity;
  enabled: boolean;
  params: Record<string, number>;
  clinical_basis: string;
  version: number;
  updated_by: string | null;
  updated_at: string;
}

// ── Specialty add-ons (Surgical / OBG / Paediatric — Phase 8, §8) ──
// `data`'s inner shape is deliberately loose (same JSONB posture as
// ip_nursing_assessments' own columns) — utils/ipSpecialtyRecords.js on the
// backend is the one place that knows the whitelisted top-level keys per
// template; the frontend form components know their own fields directly
// rather than re-deriving them from a shared schema.

export type SpecialtyTemplate = 'surgical' | 'obg' | 'paediatric';

export interface SpecialtyRecordTemplateOption {
  code: SpecialtyTemplate;
  label: string;
}

export interface SpecialtyRecord {
  id: number;
  admission_id: number;
  patient_id: number;
  template: SpecialtyTemplate;
  data: Record<string, unknown>;
  status: 'draft' | 'signed';
  signed_by: string | null;
  signed_by_user_id: number | null;
  signed_at: string | null;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

// Phase 9 (§9.4) — the admission-level legal-hold flag. 'mlc'/'death' are set
// automatically by their own routes; 'litigation'/'manual' are set by an
// MRD/Admin user via POST .../legal-hold.
export type LegalHoldSource = 'mlc' | 'death' | 'litigation' | 'manual';

export interface LegalHold {
  legal_hold: boolean;
  legal_hold_source: LegalHoldSource | null;
  legal_hold_reason: string | null;
  legal_hold_set_by: string | null;
  legal_hold_set_at: string | null;
  legal_hold_cleared_by: string | null;
  legal_hold_cleared_at: string | null;
}
